import { Group, Loader, Text } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconInfoCircleFilled } from '@tabler/icons-react'
import { useQueries } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { getLanguagesRetrieveQueryOptions, useLanguagesList } from '#/api/react-query/other'
import envStore from '#/envStore'
import { KOBO_Z_INDEX } from '#/theme/kobo/zIndex'
import KoboIcon from '../common/KoboIcon'
import Select from '../common/Select'
import type { LanguageCode } from '../languages/languagesStore'

interface LanguageSelectorProps {
  onLanguageChange: (language: LanguageCode | null) => void
  titleOverride?: string
  hiddenLanguages?: LanguageCode[]
  suggestedLanguages?: LanguageCode[]
  /** Useful to make this a controlled component if the parent wants a 'back' button to go to a previous step */
  value?: LanguageCode | null
  disabled?: boolean
  required?: boolean
  nothingFoundMessage?: string
}

const MINIMUM_SEARCH_LENGTH = 2
// Timeout chosen based on same debounce time in old languageSelector.tsx
const SEARCH_DEBOUNCE_MS = 300
const LANGUAGE_SELECTOR_SUPPORT_URL = 'transcription-translation.html#language-list'

const LanguageSelector = (props: LanguageSelectorProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS)
  // Keep UI grouping in sync with what user is typing right now.
  // This avoids a stale "Results" group after clearing the input.
  const isSearching = searchValue.length >= MINIMUM_SEARCH_LENGTH
  // Keep API calls debounced to avoid network churn while typing.
  const isDebouncedSearching = debouncedSearch.length >= MINIMUM_SEARCH_LENGTH

  // "Main" language list (either featured languages or result of searching)
  // TODO: Orval-generated LanguageListParams does not include a `q` query param but the API allows it, using `as any`
  // as a workaround. See DEV-2142
  const { data, isLoading } = useLanguagesList(isDebouncedSearching ? ({ q: debouncedSearch } as any) : undefined)
  const languages = data?.status === 200 ? data.data.results : []

  // Create a query for each suggested language
  const hasSuggestedLanguages = props.suggestedLanguages && props.suggestedLanguages.length > 0
  const suggestedLanguageQueries = useQueries({
    queries: hasSuggestedLanguages
      ? props.suggestedLanguages!.map((code) => getLanguagesRetrieveQueryOptions(code))
      : [],
  })

  const suggestedLanguages = hasSuggestedLanguages
    ? suggestedLanguageQueries
        .filter((result) => result.isSuccess)
        .flatMap((result) => (result.data?.status === 200 ? [result.data.data] : []))
    : []

  // We memoize here for a slight performance improvement, but mainly it makes the logic to group the suggested and 'main'
  // languages easier to read
  const languageOptions = useMemo(() => {
    // Show all results of a search, if there is no search, show the featured languages again
    const languagesList = isSearching ? languages : languages.filter((lang) => lang.featured)
    const allLanguages = [
      ...suggestedLanguages,
      ...languagesList.filter((lang) => !suggestedLanguages.some((suggested) => suggested.code === lang.code)),
    ]

    // Create language groups
    const suggestedItems = allLanguages
      .filter((lang) => props.suggestedLanguages?.includes(lang.code))
      .filter((lang) => !props.hiddenLanguages?.includes(lang.code))
      .map((lang) => {
        return {
          value: lang.code,
          label: `${lang.name} (${lang.code})`,
        }
      })
    const otherItems = allLanguages
      .filter((lang) => !props.suggestedLanguages?.includes(lang.code))
      .filter((lang) => !props.hiddenLanguages?.includes(lang.code))
      .map((lang) => {
        return {
          value: lang.code,
          label: `${lang.name} (${lang.code})`,
        }
      })

    // Build groups conditionally
    const groups = []
    if (suggestedItems.length > 0) {
      groups.push({
        group: t('Suggested'),
        items: suggestedItems,
      })
    }
    groups.push({
      group: isSearching ? t('Results') : '',
      items: otherItems,
    })

    return groups
  }, [languages, isSearching, props.hiddenLanguages, props.suggestedLanguages, suggestedLanguages])

  const onLanguageSelected = (selectedLanguage: string | null) => {
    const allLanguages = [...suggestedLanguages, ...languages]
    const selectedLanguageObject = allLanguages.find((lang) => lang.code === selectedLanguage) || null
    props.onLanguageChange(selectedLanguageObject?.code || null)
  }

  const openSupportPage = () => {
    window.open(envStore.data.support_url + LANGUAGE_SELECTOR_SUPPORT_URL, '_blank')
  }

  return (
    <Select
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      data={languageOptions}
      onChange={onLanguageSelected}
      value={props.value ?? null}
      label={props.titleOverride ?? t('Select a language')}
      placeholder={t('Search or select a language')}
      searchable
      clearable
      clearButtonProps={{
        // Mantine clear button is aria-hidden; give tests a stable selector.
        className: 'language-selector-clear-button',
        'aria-label': t('Clear selected language'),
      }}
      disabled={props.disabled}
      comboboxProps={{
        zIndex: KOBO_Z_INDEX.dropdown,
        resetSelectionOnOptionHover: true,
        position: 'bottom',
        middlewares: { flip: false, shift: false },
      }}
      nothingFoundMessage={
        isLoading ? undefined : (
          <Group
            onClick={openSupportPage}
            gap={'xs'}
            align='center'
            style={{ cursor: 'pointer' }}
            c='var(--mantine-color-blue-5)'
          >
            <KoboIcon icon={IconInfoCircleFilled} size='sm' />
            <Text>
              {props.nothingFoundMessage ?? t('No matching languages found. Try another spelling or language name')}
            </Text>
          </Group>
        )
      }
      renderOption={(item) => <Text>{item.option.label}</Text>}
      required={props.required}
      rightSection={isLoading ? <Loader size='xs' /> : undefined}
    />
  )
}

export default LanguageSelector
